/**
 * THE AUTH BOOT AND THE ONE HOOK EVERY SCREEN READS.
 *
 * `bootAuth()` runs ONCE, from the shell, and does three things — none of which blocks anything else:
 *
 *   · restore the inert wallet record (synchronous, local, zero network) so a returning tab paints its address
 *   · verify a stored bearer against `GET /session`
 *   · PROBE THE KEYSTORE, because a live bearer with a dead signing session is a real state — "signed in,
 *     cannot trade" — and every money button is gated on both
 *
 * The visibility keepalive lives here too, and it is not decoration: the quote refresh is visibility-gated, so
 * a backgrounded tab issues nothing and can be signed out while still rendering as signed in. The user then
 * discovers it by pressing Buy.
 *
 * ⚠️ THERE IS NO `/auth/config` ON THE TRADER SURFACE, so "is auth enabled on this server" is not a question
 * this client can ask. `SIGNER_ORG_ID` being empty is the local equivalent — signing is unconfigured in this
 * BUILD — and it is reported as exactly that rather than as a server state.
 */

import { useEffect } from "react";
import { SIGNER_ORG_ID } from "../api/config.ts";
import { type SessionView, sessionStore, setSignerState } from "../stores/session.ts";
import { useStore } from "../stores/store.ts";
import { hasStoredBearer, signOutEverywhere, verifySession } from "./session.ts";
import { clearSigningSession, configureTurnkey, hasSession } from "./turnkey.ts";
import { forgetWallet, restoreWallet } from "./wallet.ts";

/** Whether this BUILD can sign at all. False ⇒ the sign-in control explains that, instead of offering a
 *  button that fails inside the vendor SDK with an error nobody can map back to a missing env var. */
export const SIGNING_CONFIGURED = SIGNER_ORG_ID !== "";

let booted = false;

export function bootAuth(): void {
  if (booted) return;
  booted = true;

  restoreWallet();
  if (SIGNING_CONFIGURED) configureTurnkey(SIGNER_ORG_ID);

  // Settles "unknown" → signedIn / signedOut-with-a-reason. Runs even with no bearer, because the reason
  // ("never") is itself the state the top bar renders.
  void verifySession();
  void hasSession().then(setSignerState);

  // ONE listener for the whole app. Per-component visibility listeners are how three screens each issue their
  // own keepalive on the same return to the tab.
  globalThis.addEventListener?.("visibilitychange", () => {
    if (globalThis.document?.visibilityState !== "visible") return;
    if (!hasStoredBearer()) return;
    void verifySession();
    void hasSession().then(setSignerState);
  });
}

export function useSession(): SessionView {
  return useStore(sessionStore);
}

/** Mount-time boot, so nothing has to remember to call it. Idempotent. */
export function useAuthBoot(): void {
  useEffect(() => {
    bootAuth();
  }, []);
}

/** SIGN OUT — all three credentials, in order. The signing SDK is injected so this module does not drag it
 *  into the entry chunk. */
export async function signOutNow(): Promise<void> {
  await signOutEverywhere(clearSigningSession);
  forgetWallet();
}
